<template>
  <div>
    <b-field grouped>
      <b-input expanded v-model="filters.name" placeholder="Contact Name" />
      <b-input expanded v-model="filters.email" placeholder="Email address" />
      <b-input expanded v-model="filters.companyName" placeholder="Company or Facility" />
      <b-select v-model="filters.state" placeholder="State">
        <option></option>
        <option
          v-for="(abbreviation, longName) in usStates"
          :value="abbreviation"
          :key="abbreviation">
          {{longName }}
        </option>
      </b-select>

      <b-input expanded v-model="filters.city" placeholder="City" />
      <b-input expanded v-model="filters.zip" placeholder="Zip Code" />
    </b-field>
    Location Contact Name
    <letter-bar v-model="filters.name" />

    <edit-modal :show.sync="showEditModal" :contact="editingContact" @updated="search"/>
    <delete-confirmation :show.sync="showDeleteConfirmation"
                         :display-name="deletingContact.companyRolodexContactFullName"
                         @deleteConfirmed="_deleteContact" />

    <b-table :data="contactsWithLocation"
             striped
             hoverable
             :loading="isLoading">

      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p v-show="!rawContactList.length && !rawLocationList.length && !hasFilterValues">Type in a contact name to search your address book.</p>
            <p v-show="!rawContactList.length && !rawLocationList.length && !isLoading && hasFilterValues">No matching contacts were found</p>
          </div>
        </section>
      </template>

      <template slot-scope="props">
        <b-table-column class="actions-cell">
          <a href=""
             v-if="canMakeSelection"
             @click.prevent="selectContact(props.row)"
             class="button">
            Select Contact
          </a>

          <a href=""
             v-if="!canMakeSelection"
             @click.prevent="edit(props.row)"
             class="button is-info">
            <font-awesome-icon icon="edit" />
          </a>

          <a href=""
             v-if="!canMakeSelection"
             @click.prevent="deleteContactPrompt(props.row)"
             class="button is-danger">
            <font-awesome-icon icon="trash-alt" />
          </a>
        </b-table-column>

        <b-table-column field="companyRolodexContactFullName" label="Contact Name">
          {{ props.row.contact.companyRolodexContactFullName}}
        </b-table-column>

        <b-table-column field="companyRolodexContactEmailAddress" label="Email Address">
          {{ props.row.contact.companyRolodexContactEmailAddress}}
        </b-table-column>
        <b-table-column field="companyRolodexContactPhones" label="Phone Number">
          {{ props.row.contact.companyRolodexContactPhones.map(phone => formatPhoneNumber(phone.companyRolodexContactPhoneDigits)).join('\n') }}
        </b-table-column>
        <b-table-column field="location" label="Company or Facility">
          {{ props.row.location}}
        </b-table-column>

        <b-table-column field="companyRolodexJobLocationAddressStreet1" label="Street Address">
          {{ props.row.address.companyRolodexJobLocationAddressStreet1}}
          <br/>
          {{ props.row.address.companyRolodexJobLocationAddressStreet2}}
        </b-table-column>

        <b-table-column field="companyRolodexJobLocationAddressCity" label="City">
          {{ props.row.address.companyRolodexJobLocationAddressCity}}
        </b-table-column>
        <b-table-column field="companyRolodexJobLocationAddressZip" label="Zip Code">
          {{ props.row.address.companyRolodexJobLocationAddressZip}}
        </b-table-column>
      </template>
    </b-table>
  </div>
</template>

<script>
const DEBOUNCE_TIME = 300
const DEBOUNCE_OPTIONS = {
  leading: true
}

import US_STATES from '@/lookup-data/us-states'

import RolodexApi from '@/api-interface/rolordex'
import { debounce, isEmpty } from 'lodash-es'
import hasEmptyValues from '@/helpers/hasEmptyValues'
import { formatPhoneNumber } from '@/helpers/phoneNumberFormatter'
import extractContactsFromLocation from '@/helpers/extractContactsFromLocation'
import LetterBar from '@/components/LetterBar'
import DeleteConfirmation from '@/components/DeleteConfirmation'
import EditModal from '@/components/RolodexContactEdit'

export default {
  name: 'RolodexContactList',

  props: {
    canMakeSelection: {
      type: Boolean,
      default: false
    },
    prePopulated: {
      type: Object
    }
  },

  data() {
    return {
      showEditModal: false,
      showDeleteConfirmation: false,
      editingContact: {},
      deletingContact: {},
      usStates: US_STATES,
      isLoading: false,
      filters: {
        name: null,
        email: null,
        companyName: null,
        city: null,
        state: null,
        zip: null
      },
      mixedList: {
        companyRolodexContact: [],
        companyRolodexJobLocation: [],
        companyRolodexJobLocationAddress: []
      }
    }
  },

  created() {
    if (!isEmpty(this.prePopulated)) {
      this.filters = Object.assign({}, this.filters, this.prePopulated)
    }

    this.search()
  },

  components: {
    LetterBar,
    DeleteConfirmation,
    EditModal
  },

  computed: {
    hasFilterValues() {
      return !hasEmptyValues(this.filters)
    },

    rawContactList() {
      return this.mixedList.companyRolodexContact || []
    },

    rawLocationList() {
      return this.mixedList.companyRolodexJobLocation || []
    },

    rawAddressList() {
      return this.mixedList.companyRolodexJobLocationAddress || []
    },

    /**
     * Associate contacts to a location/company
     * This is necessary because the API only returns which address the user belongs to... but not what location that
     * address belongs to.
     *
     * @returns {*[]}
     */
    contactsWithLocation() {
      const contacts = extractContactsFromLocation(this.rawLocationList)

      // the `contacts` array is for ALL contacts in matching locations, but we only want to show contacts that actually
      // match the search, so we need to cross-reference this enhanced list with the actual matching contact list
      // from the API
      const matchingContactKeys = this.rawContactList.map((contact) => contact.companyRolodexContactKey)
      return contacts.filter((contact) => matchingContactKeys.includes(contact.contact.companyRolodexContactKey))
    }
  },

  methods: {
    toggleDeleteConfirmation() {
      this.showDeleteConfirmation = !this.showDeleteConfirmation
    },

    toggleEditModal() {
      this.showEditModal = !this.showEditModal
    },

    deleteContactPrompt(contact) {
      this.deletingContact = contact.contact
      this.toggleDeleteConfirmation()
    },

    async _deleteContact() {
      await RolodexApi.deleteContactByKey(this.deletingContact.companyRolodexContactKey)
      this.search()
    },

    edit(contact) {
      this.editingContact = contact.contact
      this.toggleEditModal()
    },

    selectContact(selection) {
      this.$emit('selected', selection.contact)
    },

    formatPhoneNumber: formatPhoneNumber,

    async fetchContactsForLocation() {
      this.isLoading = true
      const location = await RolodexApi.getLocationByKey({ companyRolodexJobLocationKey: this.locationKey })
      this.mixedList.companyRolodexJobLocation = [location]
      this.filters.companyName = location.companyRolodexJobLocationName
      this.isLoading = false
    },

    search: debounce(
      async function() {
        this.isLoading = true
        try {
          if (this.hasFilterValues) {
            this.mixedList = await RolodexApi.contactSearch(this.filters)
          } else {
            // if no filter values are present, perform a wild-card search
            this.mixedList = await RolodexApi.contactSearch({ name: '%' })
          }
        } catch (error) {
          console.error(`Error searching for [${this.filters}]: `, error)
        } finally {
          this.isLoading = false
        }
      },
      DEBOUNCE_TIME,
      DEBOUNCE_OPTIONS
    )
  },

  watch: {
    filters: {
      deep: true,
      handler: 'search'
    }
  }
}
</script>

<style scoped lang="scss">
.actions-cell {
  min-width: 120px;
  .button {
    margin: 0 3px;
  }
}
</style>
